import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import { useAddToast } from '../../contexts/ToastContext';
import {
  fetchAddableTeamProjectsAndReleaseGroups,
  getManualPaginatedAddableTeamProjectsAndReleaseGroupsQueryKey,
} from '../../hooks/api/teams/useAddableProjectsAndReleaseGroups';
import {
  fetchReleaseGroupAddableProjects,
  getReleaseGroupAddableProjectsQueryKey,
} from '../../hooks/api/teams/useReleaseGroupAddableProjects';
import { queryClient } from '../../ReactQuery';
import TeamsModule from '../../redux/teams';
import { hasError } from '../../utils';
import Button, { ButtonGroup } from '../generic/Button';
import { ErrorAlert } from '../generic/ErrorAlert';
import AsyncPaginatedSelect from '../generic/Select/AsyncPaginatedSelect';
import type { GetTeamResponse } from '../../../shared/types/routes/teams';
import type { SelectOption } from '../../types';

type Props = {
  team: GetTeamResponse;
  onDone(projectIds: string[], releaseGroups: number[]): void;
  onHide(): void;
};

type OptionValue = {
  type: 'project';
  id: string;
} | {
  type: 'release_group';
  id: number;
};

type SelectedOption = SelectOption<OptionValue>;

const startingPage = { page: 1 };

/**
 * The modal for adding project(s) to a team.
 *
 * @param {Props} props - The props for the modal.
 * @param {Team} props.team - The team we are adding project(s) to.
 * @param {(projectIds: string[], releaseGroups: number[]) => void} props.onDone - Method for saving the changes.
 * @param {() => void} props.onHide - Method for closing the modal/losing changes.
 */
const ModalAddTeamProjects = (props: Props) => {
  const status = useSelector(TeamsModule.selectors.getTeamUpdatingStatus);

  const addToast = useAddToast();

  const [isFetchingNextPage, setIsFetchingNextPage] = useState<boolean>(false);
  const [isFetchingGroupProjects, setIsFetchingGroupProjects] = useState<boolean>(false);

  const loadPage = async (searchString: string, _loaded: any, additional: { page: number } | undefined) => {
    setIsFetchingNextPage(true);
    // Note: we should almost always use the typical hook based approach for react-query with `useQuery`, but in this case
    // since we need to imperitavely fetch the data in this loadPage function so that we can return it back out, we need
    // to directly fetch with `ensureQueryData` instead of using `useQuery`.
    try {
      const res = await queryClient.ensureQueryData({
        queryKey: getManualPaginatedAddableTeamProjectsAndReleaseGroupsQueryKey(
          props.team.id,
          20,
          additional?.page ?? 0,
          searchString,
        ),
        queryFn: () => fetchAddableTeamProjectsAndReleaseGroups(
          { pageParam: additional?.page ?? 0 },
          props.team.id,
          20,
          searchString,
        ),
      });
      setIsFetchingNextPage(false);
      return {
        options:
          res.data?.results.map((projOrRg) => ({
            label: projOrRg.title,
            value: projOrRg.type === 'release_group'
              ? { type: 'release_group' as const, id: Number(projOrRg.id) }
              : { type: 'project' as const, id: projOrRg.id },
          })) ?? [],
        hasMore: (res.data?.totalCount ?? 0) > (res.data?.page ?? 0) * (res.data?.pageSize ?? 0),
        additional: {
          page: (additional?.page ?? 0) + 1,
        },
      };
    } catch (err) {
      setIsFetchingNextPage(false);
      addToast({
        type: 'error',
        content: 'Failed to fetch more projects and release groups.',
      });
      return {
        options: [],
        hasMore: false,
        additional: {
          page: (additional?.page ?? 0) + 1,
        },
      };
    }
  };

  const [selectedOptions, setSelectedOptions] = useState<SelectedOption[]>([]);

  // checkedReleaseGroups will say for which release groups we've fetched all projects to be included among the
  // projects in the team.
  const [checkedReleaseGroups, setCheckedReleaseGroups] = useState<Set<number>>(new Set());

  const filterProjectsAndReleaseGroups = (options: Array<{ label: string; value: OptionValue }>) => {
    const projectIds: string[] = [];
    const releaseGroupIds: number[] = [];

    options.forEach(({ value }) => {
      if (value.type === 'project') {
        projectIds.push(value.id);
      } else {
        releaseGroupIds.push(value.id);
      }
    });

    props.onDone(projectIds, releaseGroupIds);
  };

  const updateSelectedOptions = async (options: Array<{ label: string; value: OptionValue }>) => {
    const newCheckedReleaseGroups = new Set<number>();
    const alreadySelectedProjects = new Set<string>(
      options.filter((opt) => opt.value.type === 'project').map((opt) => opt.value.id as string),
    );

    const releaseGroupsToFetch = options
      .filter((opt) => opt.value.type === 'release_group' && !checkedReleaseGroups.has(opt.value.id))
      .map((opt) => opt.value.id as number);

    setIsFetchingGroupProjects(true);
    try {
      const projects = await Promise.all(releaseGroupsToFetch.map((rg) => {
        return queryClient.ensureQueryData({
          queryKey: getReleaseGroupAddableProjectsQueryKey(props.team.id, rg),
          queryFn: () =>
            fetchReleaseGroupAddableProjects(props.team.id, rg),
        });
      }));

      projects.forEach((resp) => {
        resp.data?.results.forEach((project) => {
          // If the project or release group is already selected, don't add it to the options.
          if (alreadySelectedProjects.has(project.id)) {
            return;
          }
          // Add the project or release group to the options.
          options.push({
            label: project.title,
            value: { type: 'project', id: project.id },
          });
        });
      });
      setIsFetchingGroupProjects(false);
    } catch (err) {
      setIsFetchingGroupProjects(false);
      addToast({
        type: 'error',
        content: 'Failed to fetch projects for the Release Group(s).',
      });
      return;
    }

    setSelectedOptions(options);
    setCheckedReleaseGroups(newCheckedReleaseGroups);
  };

  return (
    <Modal show onHide={props.onHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          Add Projects to {props.team.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {hasError(status) && <ErrorAlert status={status} />}
        <AsyncPaginatedSelect
          isMulti
          onChange={updateSelectedOptions}
          loadOptions={loadPage}
          value={selectedOptions}
          additional={startingPage}
          debounceTimeout={200}
          isLoading={isFetchingNextPage || isFetchingGroupProjects}
          isDisabled={isFetchingGroupProjects}
          />
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button
            type="secondary"
            size="small"
            onClick={props.onHide}
            isDisabled={status.loading}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="small"
            onClick={() => filterProjectsAndReleaseGroups(selectedOptions)}
            isDisabled={status.loading || !selectedOptions.length}
          >
            Add
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalAddTeamProjects;
